import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, Send, Megaphone, Sparkles, Trash2, MessageCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts";
import { apiCall } from "../lib/api";
import { toast } from "sonner";

const btn3D = "shadow-[0_6px_0_rgba(0,0,0,0.12),0_8px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_0_rgba(0,0,0,0.12),0_6px_12px_rgba(0,0,0,0.06)] hover:translate-y-[2px] active:shadow-[0_1px_0_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.04)] active:translate-y-[5px] transition-all duration-150 ease-out";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "shoutout", label: "Shoutouts" },
  { id: "appreciation", label: "Appreciation" },
  { id: "motivation", label: "Motivation" },
  { id: "confession", label: "Confessions" },
];

const CATEGORY_COLORS = {
  shoutout: "bg-blue-100 text-blue-700",
  appreciation: "bg-green-100 text-green-700",
  motivation: "bg-purple-100 text-purple-700",
  confession: "bg-pink-100 text-pink-700",
};

const ShoutoutBoardPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("shoutout");
  const [recipient, setRecipient] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchPosts = async () => {
    try {
      const data = await apiCall("GET", `/shoutouts?category=${filter}`);
      setPosts(data);
    } catch (err) { console.error("Failed to fetch posts:", err); }
    setLoading(false);
  };

  useEffect(() => { fetchPosts(); }, [filter]);

  const handleSubmit = async () => {
    if (!user) { toast.error("Please sign in to post"); return; }
    if (content.trim().length < 5) { toast.error("Message too short"); return; }
    setSubmitting(true);
    try {
      await apiCall("POST", "/shoutouts", { content, category, recipient_name: recipient || null }, token);
      toast.success("Posted successfully!");
      setContent("");
      setRecipient("");
      fetchPosts();
    } catch (err) { toast.error("Failed to post"); }
    setSubmitting(false);
  };

  const handleLike = async (id) => {
    if (!user) { toast.error("Please sign in to like"); return; }
    try {
      await apiCall("POST", `/shoutouts/${id}/like`, null, token);
      fetchPosts();
    } catch (err) { toast.error("Failed to like"); }
  };

  const handleDelete = async (id) => {
    if (!user) return;
    try {
      await apiCall("DELETE", `/shoutouts/${id}`, null, token);
      toast.success("Post deleted");
      fetchPosts();
    } catch (err) { toast.error("Failed to delete"); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="shoutout-board-page">
      <div className="bg-gradient-to-r from-pink-600 to-rose-700 text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <button onClick={() => navigate(-1)} className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl mb-4 border border-white/20 ${btn3D} cursor-pointer select-none`} data-testid="back-btn">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-3">
            <Megaphone size={28} />
            <div>
              <h1 className="text-2xl font-bold">Shoutout Board</h1>
              <p className="text-white/70 text-sm">Celebrate, appreciate, and motivate each other</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Compose */}
        {user && (
          <Card className="shadow-lg border-0" data-testid="compose-card">
            <CardContent className="p-5 space-y-3">
              <div className="flex gap-2 flex-wrap">
                {CATEGORIES.filter(c => c.id !== "all").map(c => (
                  <button key={c.id} onClick={() => setCategory(c.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium ${category === c.id ? "bg-[#002147] text-white" : "bg-gray-100 text-gray-600"} transition-colors`}
                    data-testid={`compose-cat-${c.id}`}>
                    {c.label}
                  </button>
                ))}
              </div>
              {(category === "shoutout" || category === "appreciation") && (
                <input type="text" value={recipient} onChange={e => setRecipient(e.target.value)}
                  placeholder="Who is this for? (optional)"
                  className="w-full px-4 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                  data-testid="recipient-input" />
              )}
              <textarea value={content} onChange={e => setContent(e.target.value)}
                placeholder="Write your message... (5-500 characters)"
                rows={3} maxLength={500}
                className="w-full px-4 py-3 border rounded-xl text-sm focus:ring-2 focus:ring-pink-400 focus:border-transparent resize-none"
                data-testid="content-input" />
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">{content.length}/500</span>
                <button onClick={handleSubmit} disabled={submitting}
                  className={`px-6 py-2.5 bg-pink-600 text-white font-medium rounded-xl flex items-center gap-2 ${btn3D} disabled:opacity-50`}
                  data-testid="submit-post-btn">
                  <Send size={14} /> {submitting ? "Posting..." : "Post"}
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 flex-wrap" data-testid="filter-tabs">
          {CATEGORIES.map(c => (
            <button key={c.id} onClick={() => setFilter(c.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium ${filter === c.id ? "bg-[#002147] text-white" : "bg-white/80 text-[#002147]"} ${btn3D}`}
              data-testid={`filter-${c.id}`}>
              {c.label}
            </button>
          ))}
        </div>

        {/* Posts */}
        <div className="space-y-4">
          {posts.map((post) => (
            <Card key={post.id} className="hover:shadow-lg transition-shadow" data-testid={`post-${post.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge className={CATEGORY_COLORS[post.category] || "bg-gray-100 text-gray-700"}>{post.category}</Badge>
                    {post.recipient_name && <span className="text-xs text-gray-500">To: <strong>{post.recipient_name}</strong></span>}
                  </div>
                  <span className="text-xs text-gray-400">{new Date(post.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-[#002147] leading-relaxed">{post.content}</p>
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
                  <button onClick={() => handleLike(post.id)}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-pink-600 transition-colors"
                    data-testid={`like-${post.id}`}>
                    <Heart size={16} className={post.liked_by?.includes(user?.id) ? "fill-pink-600 text-pink-600" : ""} />
                    <span>{post.likes || 0}</span>
                  </button>
                  {user && (user.user_type === "admin" || user.user_type === "teacher" || post.author_id === user.id) && (
                    <button onClick={() => handleDelete(post.id)}
                      className="flex items-center gap-1 text-sm text-gray-400 hover:text-red-600 transition-colors ml-auto"
                      data-testid={`delete-${post.id}`}>
                      <Trash2 size={14} /> Delete
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {!loading && posts.length === 0 && (
            <div className="text-center py-12">
              <MessageCircle size={48} className="mx-auto text-[#002147]/20 mb-3" />
              <p className="text-[#002147]/50 font-medium">No posts yet. Be the first to share!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShoutoutBoardPage;
